import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    UntypedFormBuilder,
    UntypedFormControl,
    Validators
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { ReplaySubject, Subject } from 'rxjs';
import { first, take, takeUntil } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { ClientField } from '../../../domain/models';
import { Selection } from '../../../selection/models';
import { SelectionService } from '../../../selection/services';
import { YourEducationForm } from '../../models';

@Component({
    selector: 'app-your-education-form',
    templateUrl: './your-education-form.component.html',
    styleUrls: ['./your-education-form.component.scss'],
    standalone: false
})
export class YourEducationFormComponent implements OnInit, AfterViewInit {
    education = YourEducationForm.initialize(this._fb);
    floatLabelControl = new UntypedFormControl('auto');
    currentYear = new Date().getFullYear();
    is16or17YearsOld: boolean = false;
    highestLevelOfEducationSelections$ =
        this.selectionService.highestLevelOfEducationSelections$;
    highestLevelOfEducationAltSelections$ =
        this.selectionService.highestLevelOfEducationAltSelections$;
    schoolingInUSSelections$ = this.selectionService.schoolingInUSSelections$;
    primaryLanguageSelections$ =
        this.selectionService.primaryLanguageSelections$;
    reasonsForAttendingSelections$ =
        this.selectionService.reasonsForAttendingSelections$;
    programSelections$ = this.selectionService.programSelections$;
    programSingleSelectSelections$ =
        this.selectionService.programSingleSelectSelections$;
    goalsSelections$ = this.selectionService.goalsSelections$;
    applicationLocationSelections$ =
        this.selectionService.applicationLocationSelections$;

    desiredScheduleSelections$ =
        this.selectionService.desiredScheduleSelections$;

    reasonMinorAttendingAdultEducationSelections$ =
        this.selectionService.reasonMinorAttendingAdultEducationSelections$;
    lastSchoolAttendedSelections$ =
        this.selectionService.lastSchoolAttendedSelections$;
    reasonLeftSchoolSelections$ =
        this.selectionService.reasonLeftSchoolSelections$;
    schoolStatusAtEntrySelections$ =
        this.selectionService.schoolStatusAtEntrySelections$;

    private lastSchoolAttendedList: Selection[] = [];

    @Input() submitting: boolean = false;

    @Input()
    set fields(value: ClientField[] | null) {
        if (value) YourEducationForm.setFields(value, this.education);
    }

    @Input()
    set value(v: any) {
        this.education.patchValue({ ...v });

        if (this.education.value?.is16or17YearsOld) {
            this.is16or17YearsOld = true;
            this.reasonMinorAttendingAdultEducationSelectionId?.addValidators([
                Validators.required
            ]);
            this.lastSchoolAttendedSelectionId?.addValidators([
                Validators.required
            ]);
        } else {
            this.is16or17YearsOld = false;
            this.reasonMinorAttendingAdultEducationSelectionId?.removeValidators(
                [Validators.required]
            );
            this.lastSchoolAttendedSelectionId?.removeValidators([
                Validators.required
            ]);
        }

        this.reasonMinorAttendingAdultEducationSelectionId?.updateValueAndValidity();
        this.lastSchoolAttendedSelectionId?.updateValueAndValidity();
    }

    @Output()
    submitted = new EventEmitter<any>();

    /** control for the MatSelect filter keyword */
    public filterLastSchoolAttendedCtrl: FormControl<any> =
        new FormControl<string>('');
    public filteredLastSchoolAttendedSelections: ReplaySubject<Selection[]> =
        new ReplaySubject<Selection[]>();
    @ViewChild('lastSchoolAttendedSelection', { static: true })
    lastSchoolAttendedSelection!: MatSelect;
    protected _onDestroy = new Subject<void>();

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly _transloco: TranslocoService,
        private readonly snackBar: MatSnackBar,
        private readonly selectionService: SelectionService,
        private readonly _googleAnalyticsService: GoogleAnalyticsService
    ) {}

    ngOnInit() {
        // load the initial last school attended list
        this.lastSchoolAttendedSelections$
            .pipe(takeUntil(this._onDestroy))
            .subscribe((selections) => {
                this.lastSchoolAttendedList = selections;
                this.filteredLastSchoolAttendedSelections.next(
                    selections.slice()
                );
            });

        // listen for search field value changes
        this.filterLastSchoolAttendedCtrl.valueChanges
            .pipe(takeUntil(this._onDestroy))
            .subscribe(() => {
                this.filterLastSchoolAttended();
            });
    }

    ngAfterViewInit() {
        // Set compareWith function after view is initialized and data is loaded
        this.lastSchoolAttendedSelections$
            .pipe(takeUntil(this._onDestroy), take(1))
            .subscribe(() => {
                if (this.lastSchoolAttendedSelection) {
                    this.lastSchoolAttendedSelection.compareWith = (
                        a: any,
                        b: any
                    ) => {
                        if (a && b) {
                            const aValue =
                                typeof a === 'object' ? a.selectionId : a;
                            const bValue =
                                typeof b === 'object' ? b.selectionId : b;
                            return Number(aValue) === Number(bValue);
                        }
                        return a === b;
                    };
                }
            });
    }

    get touched() {
        return this.education.touched;
    }

    get highestLevelOfEducationSelectionId(): AbstractControl | null {
        return this.education.get('highestLevelOfEducationSelectionId');
    }

    get highestLevelOfEducationAltSelectionId(): AbstractControl | null {
        return this.education.get('highestLevelOfEducationAltSelectionId');
    }

    get reasonMinorAttendingAdultEducationSelectionId(): AbstractControl | null {
        return this.education.get(
            'reasonMinorAttendingAdultEducationSelectionId'
        );
    }
    get lastSchoolAttendedSelectionId(): AbstractControl | null {
        return this.education.get('lastSchoolAttendedSelectionId');
    }
    get reasonLeftSchoolSelectionId(): AbstractControl | null {
        return this.education.get('reasonLeftSchoolSelectionId');
    }
    get schoolStatusAtEntrySelectionId(): AbstractControl | null {
        return this.education.get('schoolStatusAtEntrySelectionId');
    }

    get lastSchoolYear(): AbstractControl | null {
        return this.education.get('lastSchoolYear');
    }

    get lastSchoolAttended(): AbstractControl | null {
        return this.education.get('lastSchoolAttended');
    }

    get lastSchoolLocation(): AbstractControl | null {
        return this.education.get('lastSchoolLocation');
    }

    get lastSchoolReasonForLeaving(): AbstractControl | null {
        return this.education.get('lastSchoolReasonForLeaving');
    }

    get schoolingInUSSelectionId(): AbstractControl | null {
        return this.education.get('schoolingInUSSelectionId');
    }

    get primaryLanguageSelectionId(): AbstractControl | null {
        return this.education.get('primaryLanguageSelectionId');
    }

    get primaryLanguageComment(): AbstractControl | null {
        return this.education.get('primaryLanguageComment');
    }

    get programSelectionIds(): AbstractControl | null {
        return this.education.get('programSelectionIds');
    }

    get programSelectionId(): AbstractControl | null {
        return this.education.get('programSelectionId');
    }

    get reasonsForAttendingSelectionIds(): AbstractControl | null {
        return this.education.get('reasonsForAttendingSelectionIds');
    }

    get reasonForAttendingOther(): AbstractControl | null {
        return this.education.get('reasonForAttendingOther');
    }

    get desiredScheduleIds(): AbstractControl | null {
        return this.education.get('desiredScheduleIds');
    }

    get goalsSelectionIds(): AbstractControl | null {
        return this.education.get('goalsSelectionIds');
    }

    get applicationLocationSelectionId(): AbstractControl | null {
        return this.education.get('applicationLocationSelectionId');
    }

    onSubmit(event: any): void {
        this.education.markAllAsTouched();
        if (this.education.valid) {
            this.submitting = true;
            this.submitted.next(this.education.value);
        } else {
            this._transloco
                .selectTranslate('application.snack.invalid-form')
                .pipe(first())
                .subscribe((message) =>
                    this.snackBar.open(message, undefined, {
                        duration: 4000
                    })
                );

            // TODO adding this log to GA to shed some light on the state of the Education form when
            // the issue described in LA-20262 is observed
            this._googleAnalyticsService.formError(this.education);
        }
    }

    protected filterLastSchoolAttended() {
        let search = this.filterLastSchoolAttendedCtrl.value;

        if (!search) {
            this.filteredLastSchoolAttendedSelections.next(
                this.lastSchoolAttendedList.slice()
            );
            return;
        } else {
            search = search.toLowerCase();
        }
        // filter the schools
        this.filteredLastSchoolAttendedSelections.next(
            this.lastSchoolAttendedList.filter(
                (school) => school.label.toLowerCase().indexOf(search) > -1
            )
        );
    }
}
